/**
 * « AUJOURD'HUI » DE L'ADMINISTRATEUR — refonte de la console, lot L1
 * (15 octobre 2026, AB#747).
 *
 * L'audit l'a mesuré : l'administrateur n'avait pas d'écran d'entrée qui lui
 * dise quoi faire, et s'il ouvrait `/ma-journee` (la file du TUTEUR), l'écran
 * restait sur « Chargement » sans fin. Celui-ci est le sien : trois tâches, et
 * pour chacune ce qui attend — sans route neuve.
 *
 *  - Relire le manuel (Contenus) : le nombre de chapitres en service ; les
 *    comptes de relecture viennent avec le tableau des chapitres (L2) ;
 *  - Le coût du modèle des sept derniers jours (Plateforme) ;
 *  - Les comptes (Comptes) ;
 *  - La rétroaction (chantier « Rétroaction », P1-J, AB#876) : les indicateurs
 *    des huit dernières semaines, la seule route neuve de cet écran
 *    (`GET /v1/admin/retroaction/indicateurs`, admin seul, lecture).
 *
 * Chaque carte se charge seule : une panne de l'une n'éteint pas les autres.
 */
import type {
  ChapitresProgrammeResponse,
  IndicateursRetroactionResponse,
  UsageIaResponse,
} from '@silex/shared';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import { CarteAntiRobots } from '../components/CarteAntiRobots';
import { CarteRetroaction } from '../components/CarteRetroaction';
import { formatNombre, formatUsd, isoJour } from '../lib/admin-format';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { useAsync } from '../lib/useAsync';

function Carte({
  titre,
  lien,
  action,
  children,
}: {
  readonly titre: string;
  readonly lien: string;
  readonly action: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="card" aria-label={titre} style={{ margin: 0 }}>
      <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>{titre}</h2>
      <div className="suivi-note" style={{ minHeight: 40 }}>
        {children}
      </div>
      <Link className="btn btn--sm" to={lien} style={{ marginTop: 10 }}>
        {action}
      </Link>
    </section>
  );
}

export function AujourdhuiAdminPage() {
  const actif = manuelVivantActif();
  const chargerChapitres = useMemo(() => (actif ? () => api.programmeChapitres() : null), [actif]);
  const chapitres = useAsync<ChapitresProgrammeResponse>(chargerChapitres);
  const chargerUsage = useCallback(
    () =>
      api.getUsageIa({ depuis: isoJour(new Date(Date.now() - 7 * 86_400_000)), groupBy: 'jour' }),
    [],
  );
  const usage = useAsync<UsageIaResponse>(chargerUsage);
  const chargerRetroaction = useCallback(() => api.indicateursRetroaction(8), []);
  const retroaction = useAsync<IndicateursRetroactionResponse>(chargerRetroaction);

  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>Aujourd’hui</h1>
          <p className="page-sub">Ce qui attend l’administration : relire, surveiller, inscrire.</p>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 14,
        }}
      >
        {actif ? (
          <Carte titre="Relire le manuel" lien="/contenus" action="Ouvrir les contenus">
            {chapitres.status === 'loading' ? 'Chargement…' : null}
            {chapitres.status === 'error' ? `Lecture impossible : ${chapitres.message}` : null}
            {chapitres.status === 'ready'
              ? `${formatNombre(chapitres.data.chapitres.length)} chapitres, dont ${formatNombre(
                  chapitres.data.chapitres.filter((c) => c.statut === 'publie').length,
                )} servis aux élèves. Relisez-les chapitre par chapitre, exercices compris.`
              : null}
          </Carte>
        ) : null}

        <Carte
          titre="Coût du modèle — 7 jours"
          lien="/plateforme/consommation"
          action="Voir la consommation"
        >
          {usage.status === 'loading' ? 'Chargement…' : null}
          {usage.status === 'error' ? `Lecture impossible : ${usage.message}` : null}
          {usage.status === 'ready'
            ? `${formatUsd(usage.data.totaux.coutTotalUsd)} pour ${formatNombre(
                usage.data.totaux.nbAppels,
              )} appels au modèle.`
            : null}
        </Carte>

        <Carte titre="Comptes" lien="/comptes" action="Ouvrir les comptes">
          Familles, élèves, tuteurs ; qui suit qui ; créer un compte.
        </Carte>

        <CarteRetroaction ressource={retroaction} />

        {/* AB#884 — les comptes signalés pour aspiration du manuel, et les blocages à lever. */}
        <CarteAntiRobots />
      </div>
    </div>
  );
}
